import { useState, useEffect } from "react";

export const useScripts = ({ async = true }) => {
  const [src, setSrc] = useState("");
  const [scriptSrcs, setScriptSrcs] = useState([]);
  const [index, setIndex] = useState();
  const [readyState, setReadyState] = useState([]);
  const [scriptsLoadStatus, setScriptsLoadStatus] = useState("idle");

  const setStatus = (value) => {
    setReadyState((prevReadyState) =>
      prevReadyState.map((status, arrayIndex) => {
        if (arrayIndex === index) {
          return {
            status: value,
          };
        }

        return status;
      })
    );
  };

  const loadScripts = (scripts = []) => {
    if (typeof scripts === "string") {
      setScriptSrcs([scripts]);

      setReadyState([
        {
          status: "idle",
        },
      ]);
    }

    if (Array.isArray(scripts)) {
      setScriptSrcs(scripts);

      const status = [...new Array(scripts.length)].map(() => ({
        status: "idle",
      }));

      setReadyState(status);
    }

    setIndex(0);
  };

  useEffect(() => {
    if (Number.isInteger(index)) {
      if (scriptSrcs.length && index < scriptSrcs.length) {
        setSrc(scriptSrcs[index]);
      }
    }
  }, [index]);

  useEffect(() => {
    if (readyState.length) {
      if (readyState[index]?.status === "ready") {
        setIndex((prevIndex) => ++prevIndex);
      }

      const ready = readyState.every(({ status }) => status === "ready");

      const loading = readyState.some(({ status }) => status === "loading");

      setScriptsLoadStatus(ready ? "ready" : loading ? "loading" : "idle");
    }
  }, [readyState]);

  useEffect(() => {
    // Allow falsy src value if waiting on other data needed for
    // constructing the script URL passed to this hook.
    if (!src) {
      return;
    }

    if (src) {
      setStatus("loading");
    }

    // Fetch existing script element by src
    // It may have been added by another instance of this hook
    let script = document.querySelector(`script[src="${src}"]`);

    if (!script) {
      // Create script
      script = document.createElement("script");
      script.src = src;
      script.async = async;
      script.setAttribute("data-status", "loading");
      // Add script to document body
      document.body.appendChild(script);

      // Store status in attribute on script
      // This can be read by other instances of this hook
      const setAttributeFromEvent = (event) => {
        script.setAttribute(
          "data-status",
          event.type === "load" ? "ready" : "error"
        );
      };

      script.addEventListener("load", setAttributeFromEvent);
      script.addEventListener("error", setAttributeFromEvent);
    } else {
      // Grab existing script status from attribute and set to state.
      setStatus(script.getAttribute("data-status"));
    }

    // Script event handler to update status in state
    // Note: Even if the script already exists we still need to add
    // event handlers to update the state for *this* hook instance.
    const setStateFromEvent = (event) => {
      setStatus(event.type === "load" ? "ready" : "error");
    };

    // Add event listeners
    script.addEventListener("load", setStateFromEvent);
    script.addEventListener("error", setStateFromEvent);

    // Remove event listeners on cleanup
    return () => {
      if (script) {
        script.removeEventListener("load", setStateFromEvent);
        script.removeEventListener("error", setStateFromEvent);
      }
    };
  }, [src]);

  return {
    loadScripts,
    scriptsLoadStatus,
  };
};
